import {
  Avatar,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

import TableError from '@/ui/components/Status/Error/TableError'
import TableBodyLoading from '@/ui/components/Status/Loading/TableBodyLoading'
import TableRowNotFound from '@/ui/components/Status/NotFound/TableRowNotFound'

import { SortableTh } from '../../../Photos/components/SortableTh'
import { useSortableData } from '../../../Photos/contexts/useSortableData'
import { useCAAMetricsContext } from '../context/CAAMetricsContext'

export default function CAAMetricsTable() {
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const numberFormat = Intl.NumberFormat(language, {
    maximumFractionDigits: 2
  })
  const { metricsQuery, showPatient } = useCAAMetricsContext()

  const { handleSort, sortDirection, sortKey, sortedData } = useSortableData(
    metricsQuery?.data?.caa ?? [],
    (item, key) => {
      if (key === 'persona')
        return item.persona.avatarName ?? item.persona.username
      return item[key] ?? 0
    }
  )

  return (
    <TableContainer
      borderRadius={'md'}
      border={'solid 1px #DEE2E6'}
      w={'full'}
      maxH={'full'}
      overflowY={'auto'}
    >
      <Table>
        <Thead
          position={'sticky'}
          top={0}
          backgroundColor={'gray.50'}
          zIndex={1}
        >
          <Tr
            backgroundColor={'#F5F5FF'}
            css={`
              & > * {
                border-bottom: solid 1px #dee2e6 !important;
                padding-top: 1rem !important;
                padding-bottom: 1rem !important;
                color: #868e96 !important;
                font-weight: 400 !important;
              }
            `}
          >
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('persona')}
              sortDirection={sortDirection}
              sortKey={'persona'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {showPatient
                  ? t('caaMetrics.table.headers.patients')
                  : t('caaMetrics.table.headers.therapists')}
              </Text>
            </SortableTh>
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('amountSessions')}
              sortDirection={sortDirection}
              sortKey={'amountSessions'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('caaMetrics.table.headers.sessions')}
              </Text>
            </SortableTh>
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('amountModeling')}
              sortDirection={sortDirection}
              sortKey={'amountModeling'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('caaMetrics.table.headers.modeling')}
              </Text>
            </SortableTh>
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('amountCustomerUse')}
              sortDirection={sortDirection}
              sortKey={'amountCustomerUse'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('caaMetrics.table.headers.customerUse')}
              </Text>
            </SortableTh>
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('accession')}
              sortDirection={sortDirection}
              sortKey={'accession'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('caaMetrics.table.headers.accession')}
              </Text>
            </SortableTh>
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('media')}
              sortDirection={sortDirection}
              sortKey={'media'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('caaMetrics.table.headers.average')}
              </Text>
            </SortableTh>
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('availability')}
              sortDirection={sortDirection}
              sortKey={'availability'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('caaMetrics.table.headers.availability')}
              </Text>
            </SortableTh>
          </Tr>
        </Thead>
        <Tbody>
          {!metricsQuery || metricsQuery.isFetching ? (
            <TableBodyLoading columns={7} rows={3} />
          ) : metricsQuery.isError ? (
            <TableError message={metricsQuery.error?.zodMessage} />
          ) : !metricsQuery.data || metricsQuery.data?.caa.length === 0 ? (
            <TableRowNotFound />
          ) : (
            sortedData.map(
              ({
                persona,
                accession,
                amountCustomerUse,
                amountModeling,
                amountSessions,
                availability,
                media
              }) => {
                return (
                  <Tr
                    key={persona.userId}
                    css={`
                      & > * {
                        padding-top: 0.5rem !important;
                        padding-bottom: 0.5rem !important;
                        border-top: solid 1px #dee2e6 !important;
                        border-bottom: solid 1px #dee2e6 !important;
                      }
                    `}
                  >
                    <Td>
                      <Flex alignItems={'center'} gap={'2'}>
                        <Avatar
                          src={persona.avatarUrl}
                          name={persona.avatarName ?? persona.username}
                          size={'xs'}
                        />
                        <Text
                          fontSize={'0.875rem'}
                          color={'custom.gray.300'}
                          fontWeight={'400'}
                        >
                          {persona?.avatarName ?? persona.username}
                        </Text>
                      </Flex>
                    </Td>
                    <Td
                      fontSize={'0.875rem'}
                      color={'custom.gray.300'}
                      fontWeight={'400'}
                    >
                      {amountSessions}
                    </Td>
                    <Td
                      fontSize={'0.875rem'}
                      color={'custom.gray.300'}
                      fontWeight={'400'}
                    >
                      {amountModeling}
                    </Td>
                    <Td
                      fontSize={'0.875rem'}
                      color={'custom.gray.300'}
                      fontWeight={'400'}
                    >
                      {amountCustomerUse}
                    </Td>
                    <Td
                      fontSize={'0.875rem'}
                      color={'custom.gray.300'}
                      fontWeight={'400'}
                    >
                      {numberFormat.format(accession)}%
                    </Td>
                    <Td
                      fontSize={'0.875rem'}
                      color={'custom.gray.300'}
                      fontWeight={'400'}
                    >
                      {t('caaMetrics.table.data.average.preffix', {
                        count: media,
                        value: numberFormat.format(media)
                      })}
                    </Td>
                    <Td
                      fontSize={'0.875rem'}
                      color={'custom.gray.300'}
                      fontWeight={'400'}
                    >
                      {numberFormat.format(availability)}%
                    </Td>
                  </Tr>
                )
              }
            )
          )}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
